import React, { Fragment, useEffect, useRef } from 'react';
import { func } from 'prop-types';
import classNames from 'classnames';

import { usePromoModel } from '../../../../contexts/PromoModelProvider';
import { usePromoCart } from '../../../../contexts/PromoCartProvider';
import { usePromoPresentation } from '../../../../contexts/PromoPresentationProvider';
import { ImageWrapper } from '../ImageWrapper/ImageWrapper';
import './ImagesContainer.scss';

export const ImagesContainer = ({ setLeftFade, setRightFade }) => {
  const { promoCartItemsObj, bogoSelectedProduct, freeItemInCart, isCartLoading } = usePromoCart();
  const {
    anchorItemId, anchorItemImage, isBogo, isMsb, isBmsm
  } = usePromoModel();
  const promoCartItems = Object.values(promoCartItemsObj || {});
  const { hideImages, promoListRef } = usePromoPresentation();
  const imageContainerRef = useRef(null);

  const setFadeOnInfoContainer = () => {
    if (imageContainerRef && imageContainerRef.current) {
      const imageContainerElement = imageContainerRef.current;
      const imageContainerRect = imageContainerElement.getBoundingClientRect();
      setLeftFade(imageContainerRect.x > imageContainerElement.firstChild?.getBoundingClientRect().x);
      setRightFade(imageContainerElement.lastChild?.getBoundingClientRect().x
        > (imageContainerRect.width + imageContainerRect.x));
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setFadeOnInfoContainer(); }, [promoCartItemsObj]);

  const scrollToProductPod = (selectedItemId) => {
    if (selectedItemId) {
      let selectedPod;
      // Ignore first item
      for (let i = 1; i < promoListRef.current.children.length; i += 1) {
        selectedPod = promoListRef.current.children[i];
        if (selectedPod.id.includes(selectedItemId)) {
          if (selectedPod?.scrollIntoView) {
            selectedPod.scrollIntoView({ behavior: 'smooth' });
          } else {
            promoListRef.current.scrollTop = selectedPod.offsetTop - selectedPod.clientHeight;
          }
          return;
        }
      }
    }
  };

  const images = [];
  if (isBogo) {
    images.push({
      url: anchorItemImage,
      itemId: anchorItemId,
      message: 'This Item',
    });

    if (freeItemInCart) {
      images.push({
        url: freeItemInCart.image,
        itemId: freeItemInCart.itemId,
        message: 'FREE'
      });
    } else if (bogoSelectedProduct) {
      images.push({
        url: bogoSelectedProduct.media.image.url,
        itemId: bogoSelectedProduct.itemId,
        message: 'FREE'
      });
    } else {
      images.push({
        message: 'FREE'
      });
    }
  }

  if (isMsb || isBmsm) {
    promoCartItems.forEach(({ itemId, image, quantity }) => {
      images.push({
        url: image,
        itemId,
        quantity
      });
    });
  }

  const imagesContainerClasses = classNames('promo-images-container', {
    'promo-images-container--hide': hideImages && (isMsb || isBmsm),
  });

  if (isCartLoading && (isMsb || isBmsm)) {
    const extendedImagesContainerSpinnerClasses = classNames(imagesContainerClasses, 'promo-images-container--loading');
    return (
      <div className={extendedImagesContainerSpinnerClasses}>
        <span className="promo-spinner" />
      </div>
    );
  }

  return (
    <>
      {images.length > 0 ? (
        <div
          className={imagesContainerClasses}
          onScroll={setFadeOnInfoContainer}
          ref={imageContainerRef}
        >
          {images.map((image, i) => (
            <Fragment key={image.title || `placeholder-${i}`}>
              <ImageWrapper
                image={image}
                isSuccessText={isBogo ? i !== 0 : null}
                showImageMessages={isBogo}
                cartQuantity={image.quantity}
                scrollToProductPod={() => scrollToProductPod(image.itemId)}
              />
              {isBogo && (i < images.length - 1) && (
                <span className="promo-image-seperator">+</span>
              )}
            </Fragment>
          ))}
        </div>
      ) : null}
    </>
  );
};

ImagesContainer.displayName = 'ImagesContainer';

ImagesContainer.propTypes = {
  setLeftFade: func,
  setRightFade: func
};

ImagesContainer.defaultProps = {
  setLeftFade: () => {},
  setRightFade: () => {}
};
